// src/utils/deviceDetection.js

/**
 * Detects if the current device is a mobile device
 * @returns {boolean} - True if the device is mobile, false otherwise
 */
export const isMobileDevice = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  // Regular expression for mobile devices
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  
  // Check if device has touch capability and matches mobile regex
  return mobileRegex.test(userAgent) || 
    (navigator.maxTouchPoints && navigator.maxTouchPoints > 2);
};

/**
 * Detects if the browser is WeChat browser
 * @returns {boolean} - True if the browser is WeChat
 */
export const isWeChatBrowser = () => {
  const userAgent = navigator.userAgent || '';
  return /MicroMessenger/i.test(userAgent);
};

/**
 * Detects if fetch API is likely blocked (adblocker or restricted browser)
 * @returns {Promise<boolean>} - True if fetch is likely blocked
 */
export const isFetchBlocked = async () => {
  try {
    // Try a simple fetch to a known-good URL
    // Use a data URL or same-origin request to test fetch availability
    const testUrl = 'data:text/plain,test';
    await fetch(testUrl);
    return false;
  } catch (error) {
    // If fetch fails even for data URLs, fetch is likely blocked
    return true;
  }
};

/**
 * Detects if the browser has restrictions that prevent blob URL creation
 * @returns {boolean} - True if blob URLs should be avoided
 */
export const shouldAvoidBlobUrls = () => {
  // WeChat browser often has issues with blob URLs
  if (isWeChatBrowser()) {
    return true;
  }
  
  // Check if URL.createObjectURL is available
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
    return true;
  }
  
  return false;
};

/**
 * Detects if direct image URLs should be used instead of fetch/blob URLs
 * @returns {Promise<boolean>} - True if direct URLs should be used
 */
export const shouldUseDirectImageUrls = async () => {
  // WeChat browser should use direct URLs
  if (isWeChatBrowser()) {
    return true;
  }
  
  // If fetch is blocked, use direct URLs
  if (await isFetchBlocked()) {
    return true;
  }
  
  // If blob URLs should be avoided, use direct URLs
  if (shouldAvoidBlobUrls()) {
    return true;
  }
  
  return false;
};

